import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { MultiSelect } from '../../../components/ui/MultiSelect';
import DateRangeCalendar from '../../../components/Calendar';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../hooks/useAuth';
import { useRemittanceList, useExportLedgers, useExportMultipleLedgersZip, useExportLedgerDetail } from '../../../hooks/useRemittance';
import { useDebounce } from '../../../hooks/useDebounce';
import { CalendarDays, RotateCcw, CopyIcon, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useModal } from '../../../hooks/useModal';
import { ExportLedgersModal } from '../../../components/ExportLedgersModal';

const statusStyles: Record<string, string> = {
  pending: 'bg-[#FFEFE3] text-[#E07A5F]', // soft peach/orange
  processing: 'bg-[#E3F2FD] text-[#1976D2]', // soft blue
  partial: 'bg-[#FFF9E6] text-[#F39C12]', // soft yellow
  paid: 'bg-[#E7F3E8] text-[#2E7D32]', // soft green
  Pending: 'bg-[#FFEFE3] text-[#E07A5F]',
  Processing: 'bg-[#E3F2FD] text-[#1976D2]',
  Partial: 'bg-[#FFF9E6] text-[#F39C12]',
  Paid: 'bg-[#E7F3E8] text-[#2E7D32]',
};

const pageSizeOptions = [
  { label: '10 / page', value: '10' },
  { label: '20 / page', value: '20' },
  { label: '50 / page', value: '50' },
  { label: '100 / page', value: '100' },
];

export default function RemittancePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = user?.roleName === 'ADMIN';
  const { open: openModal, close: closeModal, Modal } = useModal();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLedgers, setSelectedLedgers] = useState<string[]>([]);

  // Filter States
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const calendarWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarWrapperRef.current &&
        !calendarWrapperRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateForApi = (date: Date | null) => {
    if (!date) return undefined;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatDateLabel = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleResetFilters = () => {
    setSearch('');
    setStartDate(null);
    setEndDate(null);
    setSelectedStatuses([]);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLedgers([]);
  }, [debouncedSearch, startDate, endDate, selectedStatuses]);

  const { data, isLoading } = useRemittanceList(user?.id, {
    page: currentPage,
    size: rowsPerPage,
    search: debouncedSearch,
    status: selectedStatuses.join(','),
    fromDate: formatDateForApi(startDate),
    toDate: formatDateForApi(endDate),
  });

  const { mutate: exportLedgers, isPending: isExporting } = useExportLedgers();
  const { mutate: exportMultipleZip, isPending: isExportingZip } = useExportMultipleLedgersZip();
  const { mutate: exportLedgerDetail, isPending: isExportingDetail } = useExportLedgerDetail();

  const handleExport = () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    openModal({
      element: (
        <ExportLedgersModal
          onClose={closeModal}
          isExporting={isExporting}
          onExport={(start, end) => {
            exportLedgers({
              userId: user.id,
              params: {
                search: debouncedSearch || undefined,
                status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
                fromDate: formatDateForApi(start),
                toDate: formatDateForApi(end),
              },
            });
            // Note: closeModal can be called here, or the modal will just close when the download is complete if we handle onSuccess.
            // useExportLedgers doesn't currently provide a callback here directly, but we can just close it immediately.
            closeModal();
          }}
        />
      ),
      closable: true,
      className: 'max-w-xl',
    });
  };

  const rows = data?.data || [];
  const totalCount = data?.totalRecords || 0;
  const totalPages = data?.totalPages || 0;

  const handleLedgerClick = (row: any) => {
    const ledgerId = row.remittance_ledger || row.ledgerId || row.remittanceId;
    navigate(
      `/finance/remittance/${ledgerId}?status=${row.status || ''}&amount=${row.amount || ''}&date=${row.date || ''}`
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = rows.map((r: any) => r.remittance_ledger || r.ledgerId || r.remittanceId).filter(Boolean);
      setSelectedLedgers(allIds);
    } else {
      setSelectedLedgers([]);
    }
  };

  const handleSelectOne = (ledgerId: string) => {
    setSelectedLedgers((prev) =>
      prev.includes(ledgerId) ? prev.filter((id) => id !== ledgerId) : [...prev, ledgerId]
    );
  };

  const isAllSelected = rows.length > 0 && selectedLedgers.length === rows.length;

  return (
    <div className="min-h-screen bg-background px-4 lg:px-6 py-4 lg:py-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text">
            Remittance
          </h1>
          <div className="flex flex-wrap items-center gap-1 text-sm text-text3 mt-1">
            Track all your remittance details, including transferred amounts,
            dates, and payment status.
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-start gap-4">
          {/* Search */}
          <div className="w-full sm:w-80">
            <Input
              placeholder="Search by Remittance Ledger/Amount"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Date range picker */}
          <div ref={calendarWrapperRef} className="relative">
            <button
              type="button"
              aria-label="Select date range"
              onClick={() => setIsCalendarOpen((prev) => !prev)}
              className="p-2.5 rounded-lg border bg-white hover:bg-gray-50 text-teal flex items-center justify-center gap-2 cursor-pointer h-[42px]"
            >
              <CalendarDays className="h-5 w-5" />
              <span className="text-sm font-semibold hidden md:inline">
                {startDate && endDate
                  ? `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`
                  : 'Select Date Range'}
              </span>
            </button>

            {isCalendarOpen && (
              <div className="absolute z-50 top-full mt-2">
                <DateRangeCalendar
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                    if (end) setIsCalendarOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          {/* Status Multiselect */}
          <div className="w-56">
            <MultiSelect
              placeholder="Select Status"
              options={[
                { label: 'Pending', value: 'pending' },
                { label: 'Processing', value: 'processing' },
                { label: 'Partial', value: 'partial' },
                { label: 'Paid', value: 'paid' },
              ]}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
            />
          </div>

          {/* Clear Filters (Reset Button) */}
          {(search || startDate || endDate || selectedStatuses.length > 0) && (
            <Button
              variant="outline"
              onClick={handleResetFilters}
              className="text-teal hover:text-white font-semibold text-sm h-[42px] flex items-center"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}

          {/* Download Selected (ZIP) Button */}
          {selectedLedgers.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                if (user?.id) {
                  exportMultipleZip({ userId: user.id, ledgerNumbers: selectedLedgers });
                }
              }}
              disabled={isExportingZip}
              className="text-teal hover:text-white font-semibold text-sm h-[42px] flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isExportingZip ? 'Zipping...' : `Download Selected ZIP (${selectedLedgers.length})`}
            </Button>
          )}

          {/* Download Button */}
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || isLoading}
            className="text-teal hover:text-white font-semibold text-sm h-[42px] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isExporting ? 'Downloading...' : 'Download Excel'}
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto w-full pb-4">
          <div className="space-y-3 min-w-[900px] lg:min-w-full">
            {/* Detached Table Header Card */}
            <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div
                className={`grid gap-2 xl:gap-4 items-center ${admin ? '[grid-template-columns:40px_80px_repeat(6,1fr)_60px]' : '[grid-template-columns:40px_repeat(6,1fr)_60px]'}`}
              >
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={isAllSelected}
                    className="w-4 h-4 rounded border-white/50 text-teal focus:ring-white cursor-pointer accent-teal"
                  />
                </div>
                {admin && <span>CRN</span>}
                <span>Date</span>
                <span>Remittance Ledger</span>
                <span>Bank UTR ID</span>
                <span className="text-center">Count</span>
                <span className="text-center">Status</span>
                <span className="text-right">Amount</span>
                <span className="text-center">Action</span>
              </div>
            </div>

            {/* Remittance Row Cards */}
            {isLoading ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                Loading remittance records...
              </div>
            ) : rows.length === 0 ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                No remittance records found
              </div>
            ) : (
              rows.map((row: any, index: number) => (
                <div
                  key={index}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm"
                >
                  <div
                    className={`grid gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-center ${
                      admin
                        ? '[grid-template-columns:40px_80px_repeat(6,1fr)_60px]'
                        : '[grid-template-columns:40px_repeat(6,1fr)_60px]'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={selectedLedgers.includes(row.remittance_ledger || row.ledgerId || row.remittanceId)}
                        onChange={() => handleSelectOne(row.remittance_ledger || row.ledgerId || row.remittanceId)}
                        className="w-4 h-4 rounded border-gray-300 text-teal focus:ring-teal cursor-pointer accent-teal"
                      />
                    </div>

                    {/* CRN */}
                    {admin && (
                      <div className="text-left font-medium ">{row.crn}</div>
                    )}

                    {/* Date */}
                    <div className="font-medium text-text">
                      {row.date || '-'}
                    </div>

                    {/* Remittance Ledger */}
                    <div className="flex items-center justify-start gap-2 group min-h-[20px] text-left">
                      <button
                        onClick={() => handleLedgerClick(row)}
                        className="text-blue-500 underline font-medium text-left break-all"
                      >
                        {row.remittance_ledger ||
                          row.ledgerId ||
                          row.remittanceId ||
                          '-'}
                      </button>
                      {(row.remittance_ledger || row.ledgerId || row.remittanceId) && (
                        <CopyIcon
                          className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 cursor-pointer hover:text-gray-600 transition-all flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            const ledgerId = row.remittance_ledger || row.ledgerId || row.remittanceId;
                            navigator.clipboard.writeText(ledgerId);
                            toast.success('Ledger ID copied');
                          }}
                        />
                      )}
                    </div>

                    {/* Bank UTR ID */}
                    <div className="truncate text-gray-500 font-mono text-xs">
                      {row.status?.toLowerCase() === 'paid' ? (row.bank_utr_id || row.utr || '-') : '-'}
                    </div>

                    {/* Count */}
                    <div className="text-center font-medium text-text">
                      {row.count || 0}
                    </div>

                    {/* Status */}
                    <div className="flex justify-center">
                      <span
                        className={`text-[11px] px-3 py-1 rounded-full font-bold tracking-wider ${
                          statusStyles[row.status] ||
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {row.status
                          ? row.status.charAt(0).toUpperCase() +
                            row.status.slice(1)
                          : 'Unknown'}
                      </span>
                    </div>

                    {/* Amount */}
                    <div className="text-right font-bold text-text">
                      ₹
                      {row.amount
                        ? Number(row.amount).toLocaleString('en-IN')
                        : 0}
                    </div>

                    {/* Action */}
                    <div className="flex justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (user?.id) {
                            const ledgerId = row.remittance_ledger || row.ledgerId || row.remittanceId;
                            exportLedgerDetail({ userId: user.id, ledgerNumber: ledgerId, params: {} });
                          }
                        }}
                        disabled={isExportingDetail}
                        title="Download Individual Excel"
                        className="p-1.5 text-teal hover:bg-teal/10 rounded transition-colors disabled:opacity-50"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            {!isLoading && (rows.length > 0 || totalCount > 0) && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border">
                <div className="text-sm text-gray-600 font-medium">
                  Showing page {currentPage}
                  {totalCount > rowsPerPage && totalPages > 0
                    ? ` of ${totalPages}`
                    : ''}
                </div>
                <div className="flex items-center gap-3">
                  <Select
                    options={pageSizeOptions}
                    value={String(rowsPerPage)}
                    position="top"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) => Math.max(prev - 1, 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage((prev) =>
                        totalCount > rowsPerPage && totalPages > 0
                          ? Math.min(prev + 1, totalPages)
                          : prev + 1
                      );
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={
                      totalCount > rowsPerPage && totalPages > 0
                        ? currentPage >= totalPages
                        : rows.length < rowsPerPage
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Bottom Padding */}
            <div className="h-20" />
          </div>
        </div>
      </div>
      <Modal />
    </div>
  );
}
